import React, { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import Swal from 'sweetalert2'

import { getMerchantFilterOptions, getMerchantList } from './api'
import { IMerchant, IMerchantWalletBalance, MerchantsListParams } from './types'
import ModalCreateMerchant from './ModalCreateMerchant'
import styles from './MerchantsList.module.css'
import MerchantFilter from '../../../../common/filter/MerchantFilter'
import { SelectData } from '../../../../common/filter/models'
import { IsGranted, ROLE, useAuth } from '../../../auth'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { KTIcon } from '../../../../../_metronic/helpers'
import { RowKebabMenu } from '../../../../common/RowKebabMenu'
import LedgerDepositModal from '../../components/LedgerWalletBalance/LedgerDepositModal'
import { Dropdown } from 'react-bootstrap'

const PER_PAGE = 50

const ENABLED_OPTIONS = [
  { value: '', label: 'Показать всех' },
  { value: 'true', label: 'Только включенные' },
  { value: 'false', label: 'Только отключенные' },
]

// Колонок в таблице разное число: кошелёк и баланс видны не всем ролям.
const SkeletonRow: React.FC<{ columns: number }> = ({ columns }) => (
  <tr>
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i}>
        <span className='placeholder col-8 rounded'></span>
      </td>
    ))}
  </tr>
)

const MerchantsList: React.FC = () => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const canCreate = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)
  const [merchants, setMerchants] = useState<IMerchant[]>([])
  const [loading, setLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [enabledFilter, setEnabledFilter] = useState<string>('true')
  const [tokenFilter, setTokenFilter] = useState<string[]>([])
  const [filterOptions, setFilterOptions] = useState<IMerchant[] | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  // Балансы приходят вместе со списком (одним ответом) и джойнятся по id мерчанта.
  // Ключа нет — Ledger не ответил, null — у мерчанта нет активного кошелька.
  const [balances, setBalances] = useState<Record<number, IMerchantWalletBalance | null>>({})
  const [adjustTarget, setAdjustTarget] = useState<IMerchant | null>(null)
  // Колонки баланса и операции над ним — только управляющим ролям.
  const canSeeBalances = IsGranted(
    [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER],
    currentUser
  )
  const canAdjustBalance = IsGranted([ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser)

  const fetchMerchants = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const params: MerchantsListParams = { page, perPage: PER_PAGE }
      if (enabledFilter !== '') params.enabled = enabledFilter === 'true'
      if (tokenFilter.length) params.merchantTokenList = tokenFilter

      const response = await getMerchantList(params)

      if (response.data?.success) {
        setMerchants(response.data.merchants ?? [])
        setBalances(response.data.balances ?? {})
        setTotalRows(response.data.totalRows ?? 0)
      } else {
        Swal.fire({ icon: 'error', title: 'Ошибка', text: 'Не удалось загрузить мерчантов' })
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Ошибка сети', text: 'Проверьте подключение и попробуйте снова' })
    } finally {
      setLoading(false)
    }
  }, [enabledFilter, tokenFilter])

  useEffect(() => {
    fetchMerchants(currentPage)
  }, [fetchMerchants, currentPage])

  // Опции мультифильтра грузим один раз: список мерчантов короткий и почти не меняется.
  useEffect(() => {
    getMerchantFilterOptions()
      .then(setFilterOptions)
      .catch(() => setFilterOptions([]))
  }, [])

  const handleEnabledChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEnabledFilter(e.target.value)
    setCurrentPage(1)
  }

  const handleMerchantChange = (selectData: SelectData) => {
    setTokenFilter(selectData.value)
    setCurrentPage(1)
  }

  const handlePageChange = (selected: { selected: number }) => {
    setCurrentPage(selected.selected + 1)
  }

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru })
    } catch {
      return '—'
    }
  }

  // Ledger отдаёт суммы строкой со scale 4 — показываем копейки, как в карточке мерчанта.
  const formatAmount = (value: string) => Number(value).toFixed(2)

  // Ключа нет — Ledger не ответил; null — активного кошелька у мерчанта нет.
  const walletOf = (merchantId: number) => balances[merchantId]

  const renderWalletCell = (merchant: IMerchant) => {
    const wallet = walletOf(merchant.id)
    if (!wallet) {
      return (
        <td>
          <span className='text-muted fs-7'>{wallet === null ? 'Кошелёк не заведён' : '—'}</span>
        </td>
      )
    }

    return (
      <td>
        <div className='fw-semibold fs-6 text-gray-800'>
          {wallet.currency ?? '—'}
          {wallet.isBlocked && <span className='badge badge-light-danger ms-2'>Заблокирован</span>}
        </div>
        <div className='text-muted fs-7'>{wallet.walletId}</div>
      </td>
    )
  }

  const renderBalanceCell = (merchant: IMerchant) => {
    const wallet = walletOf(merchant.id)
    if (!wallet) {
      return (
        <td>
          <span className='text-muted fs-7'>—</span>
        </td>
      )
    }

    return (
      <td>
        <div className='fw-bolder fs-6 text-gray-900'>{formatAmount(wallet.availableBalance)}</div>
        <div className='text-muted fs-7'>Кредит: {formatAmount(wallet.creditBalance)}</div>
        <div className='text-muted fs-7'>Холд: {formatAmount(wallet.debitsPending)}</div>
      </td>
    )
  }

  const countPages = Math.ceil(totalRows / PER_PAGE)

  return (
    <div>
      <div className='card mb-7'>
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Мерчанты</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>Всего: {totalRows}</span>
          </h3>
          {canCreate && (
            <div className='card-toolbar'>
              <button type='button' className='btn btn-sm btn-primary' onClick={() => setCreateOpen(true)}>
                <KTIcon iconName='plus' className='fs-2' /> Создать мерчанта
              </button>
            </div>
          )}
        </div>
      </div>

      <div className='card'>
        <div className='card-header border-0 pt-6 pb-4'>
          <div className='card-toolbar d-flex flex-wrap gap-3 align-items-center'>
            <select
              className='form-select form-select-solid w-auto'
              value={enabledFilter}
              onChange={handleEnabledChange}
            >
              {ENABLED_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <div className='w-350px'>
              {filterOptions === null ? (
                <span className='text-muted'>Загрузка мерчантов...</span>
              ) : (
                <MerchantFilter
                  changeSelect={handleMerchantChange}
                  value={tokenFilter}
                  isMultiple={true}
                  preloadData={filterOptions}
                />
              )}
            </div>
          </div>
        </div>

        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                <th>Мерчант</th>
                {canSeeBalances && <th>Кошелёк</th>}
                {canSeeBalances && <th>Баланс</th>}
                <th>Дата добавления</th>
                <th>Дата редактирования</th>
                <th>Кол-во операторов</th>
                <th className='text-end'></th>
              </tr>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <SkeletonRow key={i} columns={canSeeBalances ? 7 : 5} />
                  ))
                : merchants.map((merchant) => (
                    <tr key={merchant.token}>
                      <td>
                        <a
                          className={styles.MerchantLink}
                          href={`/settings/merchants/${merchant.id}`}
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(`/settings/merchants/${merchant.id}`)
                          }}
                        >
                          <div className='text-dark fw-bold fs-6 d-flex align-items-center gap-2'>
                            <span className={styles.MerchantTitle}>{merchant.title}</span>
                            <span
                              className={`badge ${merchant.enabled ? 'badge-light-success' : styles.DisabledBadge}`}
                            >
                              {merchant.enabled ? 'Включён' : 'Отключён'}
                            </span>
                          </div>
                          <div className='text-muted fs-7'>{merchant.token}</div>
                        </a>
                      </td>
                      {canSeeBalances && renderWalletCell(merchant)}
                      {canSeeBalances && renderBalanceCell(merchant)}
                      <td>
                        <span className='text-muted fw-semibold'>{formatDate(merchant.createdAt)}</span>
                      </td>
                      <td>
                        <span className='text-muted fw-semibold'>{formatDate(merchant.updatedAt)}</span>
                      </td>
                      <td>
                        <span className='fw-semibold'>{merchant.operators?.length ?? 0}</span>
                      </td>
                      <td className='text-end'>
                        <RowKebabMenu title='Действия по мерчанту'>
                          <Dropdown.Item
                            as='button'
                            type='button'
                            className='d-flex align-items-center'
                            onClick={() => navigate(`/settings/merchants/${merchant.id}`)}
                          >
                            <KTIcon iconName='pencil' className='fs-3 me-2' />
                            Изменить
                          </Dropdown.Item>
                          {canAdjustBalance && (
                            <Dropdown.Item
                              as='button'
                              type='button'
                              className='d-flex align-items-center'
                              onClick={() => setAdjustTarget(merchant)}
                            >
                              <KTIcon iconName='dollar' className='fs-3 me-2' />
                              Операция над балансом
                            </Dropdown.Item>
                          )}
                          {canSeeBalances && (
                            <Dropdown.Item
                              as='button'
                              type='button'
                              className='d-flex align-items-center'
                              onClick={() => navigate(`/settings/merchants/${merchant.id}/ledger-history`)}
                            >
                              <KTIcon iconName='book' className='fs-3 me-2' />
                              История движения баланса
                            </Dropdown.Item>
                          )}
                        </RowKebabMenu>
                      </td>
                    </tr>
                  ))}
            </TableBody>
          </Table>

          {!loading && merchants.length === 0 && (
            <div className='d-flex justify-content-center py-10'>
              <span className='text-muted fs-5'>Мерчанты не найдены</span>
            </div>
          )}

          {countPages > 1 && (
            <div className='d-flex justify-content-center mt-6'>
              <ReactPaginate
                previousLabel='‹'
                nextLabel='›'
                breakLabel='...'
                pageCount={countPages}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageChange}
                forcePage={currentPage - 1}
                containerClassName='pagination'
                pageClassName='page-item'
                pageLinkClassName='page-link'
                previousClassName='page-item'
                previousLinkClassName='page-link'
                nextClassName='page-item'
                nextLinkClassName='page-link'
                breakClassName='page-item'
                breakLinkClassName='page-link'
                activeClassName='active'
              />
            </div>
          )}
        </div>
      </div>

      {canCreate && (
        <ModalCreateMerchant
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => fetchMerchants(currentPage)}
        />
      )}

      {canAdjustBalance && adjustTarget && (
        <LedgerDepositModal
          ownerType='merchant'
          id={adjustTarget.id}
          isOpen={true}
          onClose={() => setAdjustTarget(null)}
          // Список перезапрашивается целиком: балансы приходят вместе с ним.
          onSuccess={() => fetchMerchants(currentPage)}
          // В строке показан кошелёк активной валюты — тот, на который идёт операция.
          availableBalance={balances[adjustTarget.id]?.availableBalance}
          currency={balances[adjustTarget.id]?.currency}
        />
      )}
    </div>
  )
}

export default MerchantsList
